/**
 * BuildTracePanel — the per-version build PIPELINE GRAPH (presentation round
 * 3, owner spec): a real node-and-edge flowchart projected from trace.json —
 * every step/LLM call is a NODE carrying its type, model + settings, inputs
 * and outputs, and +offset datetime; directed SVG ARROWS carry the flow,
 * including the gate-failure feedback loop back into the next repair round and
 * the parallel asset branch converging at the bind step. Edges are drawn from
 * measured DOM positions (ResizeObserver-refreshed) — deterministic per trace,
 * no graph library. The raw chronological timeline stays under an expander:
 * same steps, one source of truth, two projections.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { BuildTraceView } from '../services/types.js';
import { useServices } from '../services/context.js';

type Step = BuildTraceView['steps'][number];

const fmtDur = (ms: number): string => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);
const fmtOffset = (t: number, start: number): string => `+${((t - start) / 1000).toFixed(1)}s`;
const fmtCost = (usd: number): string => `$${usd.toFixed(usd >= 0.1 ? 2 : 4)}`;
const fmtClock = (t: number): string => new Date(t).toLocaleTimeString();

/** Lazy expander: fetches the trace on first open (a callable, not a
 *  subscription — traces are immutable once the build is terminal). */
export function BuildTraceExpander({
  creationId,
  version,
  available,
}: {
  creationId: string;
  version: number;
  /** The version doc carries traceGcsUri (absent = predates tracing). */
  available: boolean;
}) {
  const services = useServices();
  const [trace, setTrace] = useState<BuildTraceView | null>(null);
  const [redacted, setRedacted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  if (!available) return null;

  const load = async (refresh = false): Promise<void> => {
    if ((!refresh && trace) || loading) return;
    setLoading(true);
    setError('');
    try {
      const res = await services.callable.getBuildTrace({ creationId, version });
      setTrace(res.trace);
      setRedacted(res.redacted);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <details
      data-testid={`build-trace-${version}`}
      style={{ margin: '6px 0 0' }}
      onToggle={(e) => {
        if ((e.target as HTMLDetailsElement).open) void load();
      }}
    >
      <summary className="muted" style={{ cursor: 'pointer', fontSize: 12 }}>
        Build trace
        <Link
          to={`/studio/g/${creationId}/trace/${version}`}
          data-testid={`open-full-trace-${version}`}
          style={{ marginLeft: 8, fontSize: 11.5 }}
          onClick={(e) => e.stopPropagation()}
        >
          Open full trace ↗
        </Link>
      </summary>
      {loading && (
        <p className="muted" style={{ fontSize: 12 }}>
          Loading trace…
        </p>
      )}
      {error && (
        <p className="error-text" style={{ fontSize: 12 }}>
          {error}
        </p>
      )}
      {trace && (
        <>
          {trace.status === 'running' && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => void load(true)} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh retained drafts'}
            </button>
          )}
          <BuildTraceGraph creationId={creationId} version={version} trace={trace} redacted={redacted} />
        </>
      )}
    </details>
  );
}

// ── graph model ───────────────────────────────────────────────────────────────

interface GraphNode {
  id: string;
  /** Eyebrow label ("LLM · claude-opus-4-8", "GATES", …). */
  kind: string;
  title: string;
  at?: string;
  tone?: 'ok' | 'bad' | 'accent';
  /** Input lines (rendered with ◂) and output lines (▸). */
  inputs?: string[];
  outputs?: string[];
  /** Free node body (gate chips, llm stats…). */
  body?: React.ReactNode;
  /** Grid placement. */
  row: number;
  lane: 1 | 2;
  rowSpan?: number;
  fullWidth?: boolean;
}

interface GraphEdge {
  from: string;
  to: string;
  label?: string;
  tone?: 'bad' | 'muted';
  /** Backward edges (repair loop) route around the left gutter. */
  back?: boolean;
}

function GateChips({ gates }: { gates: Step[] }) {
  return (
    <div>
      {gates.map((g, i) => {
        const ok = g.ok === true;
        return (
          <span
            key={i}
            title={
              typeof g.outputDigest === 'string' && g.outputDigest
                ? g.outputDigest
                : `${String(g.name)} · ${fmtDur(Number(g.durationMs ?? 0))}`
            }
            style={{
              display: 'inline-block',
              fontSize: 10.5,
              padding: '1px 6px',
              margin: '2px 3px 0 0',
              borderRadius: 10,
              border: `1px solid ${ok ? 'var(--ok, #30a46c)' : 'var(--danger, #e5484d)'}`,
              color: ok ? 'var(--ok, #30a46c)' : 'var(--danger, #e5484d)',
            }}
          >
            {String(g.name)} {ok ? '✓' : '✗'}
          </span>
        );
      })}
    </div>
  );
}

const llmStats = (s: Step): string =>
  `${String(s.inputTokens)}→${String(s.outputTokens)} tok${
    typeof s.estimatedCostUsd === 'number' ? ` · ${fmtCost(s.estimatedCostUsd)}` : ''
  }${typeof s.durationMs === 'number' ? ` · ${fmtDur(s.durationMs)}` : ''} · cap ${String(s.maxTokens ?? '—')} · ${String(s.stopReason)}`;

/** Project the append-ordered trace steps into nodes + directed edges. */
function buildGraph(
  trace: BuildTraceView,
  onPrompt: (uri: string) => void,
): { nodes: GraphNode[]; edges: GraphEdge[] } {
  const start = trace.startedAt;
  const steps = trace.steps;
  const milestone = (name: string): Step | undefined =>
    steps.find((s) => s.type === 'milestone' && s.name === name);
  const llmSteps = steps.filter((s) => s.type === 'llm');
  const buildLlm = llmSteps.filter((s) => s.task === 'build' || s.task === 'iterate' || s.task === 'repair-fast');
  const editRounds = steps.filter((s) => s.type === 'edit-round');
  const roundGates = (r: number): Step[] =>
    steps.filter((s) => s.type === 'gate' && s.phase === 'round' && Number(s.round) === r);
  const regateGates = steps.filter((s) => s.type === 'gate' && s.phase === 're-gate');
  const copyGates = steps.filter((s) => s.type === 'gate' && s.phase === 'round' && Number(s.round) === 0);
  const diagnoses = steps.filter((s) => s.type === 'diagnosis');
  const safeties = steps.filter((s) => s.type === 'safety-review');
  const qualityPasses = steps.filter((s) => s.type === 'quality-pass');
  const review = llmSteps.find((s) => s.task === 'review');
  const critic = llmSteps.find((s) => s.task === 'critic');
  const amend = llmSteps.find((s) => s.task === 'amend');
  const seeded = milestone('seeded');
  const outcomes = steps.filter((s) => s.type === 'asset-outcome');
  const byOutcome = (o: string): string[] =>
    outcomes.filter((s) => s.outcome === o).map((s) => String(s.name));
  const generated = byOutcome('generated');
  const reused = byOutcome('reused');
  const banked = byOutcome('banked');
  const preserved = byOutcome('preserved');
  const budget = milestone('asset-budget-expired');
  const inject = steps.find((s) => s.type === 'asset-inject');
  const unbound = steps.find((s) => s.type === 'asset-inject-unbound');
  const artifacts = steps.filter((s) => s.type === 'artifact' && s.kind !== 'trace');
  const errors = steps.filter((s) => s.type === 'error');
  const artCompletion = milestone('art-completion-enqueued');
  const cover = milestone('cover-art');
  const suggest = llmSteps.find((s) => s.task === 'suggest');

  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];
  let row = 1;

  const promptBtn = (s: Step): React.ReactNode =>
    typeof s.promptGcsUri === 'string' && s.promptGcsUri ? (
      <button
        className="btn btn--sm"
        style={{ marginLeft: 6, fontSize: 10.5 }}
        onClick={() => onPrompt(s.promptGcsUri as string)}
      >
        prompt
      </button>
    ) : null;

  // INPUTS
  nodes.push({
    id: 'inputs',
    kind: 'INPUTS',
    title: trace.mode === 'copy' ? 'seed source archive' : 'build request',
    tone: 'accent',
    row: row++,
    lane: 1,
    inputs:
      trace.mode === 'copy'
        ? [`src.tgz${seeded ? ` (${String(seeded.from)})` : ''}`]
        : [
            'change request (creator prompt)',
            'game design + settings spec',
            'attached assets',
            ...(seeded ? [`seed: ${String(seeded.from)}`] : []),
          ],
  });

  let prev = 'inputs';

  if (amend) {
    nodes.push({
      id: 'amend',
      kind: `LLM amend · ${String(amend.model)}`,
      title: 'design registrar',
      at: fmtOffset(amend.t, start),
      row: row++,
      lane: 1,
      body: (
        <div style={{ fontSize: 11.5 }} title={typeof amend.promptPreview === 'string' ? amend.promptPreview : undefined}>
          <span className="muted">{llmStats(amend)}</span>
          {promptBtn(amend)}
        </div>
      ),
      outputs: ['amended design (or unchanged)'],
    });
    edges.push({ from: prev, to: 'amend' });
    prev = 'amend';
  }

  // CODE TRACK rounds: llm_r → gates_r; fail loops back into llm_{r+1}
  const rounds = [...new Set(editRounds.map((s) => Number(s.round)))].sort((a, b) => a - b);
  const effRounds = rounds.length > 0 ? rounds : buildLlm.map((_, i) => i + 1);
  effRounds.forEach((r, i) => {
    const llm = buildLlm[i];
    const er = editRounds.find((s) => Number(s.round) === r);
    const files = Array.isArray(er?.filesWritten) ? (er!.filesWritten as string[]) : [];
    const gates = roundGates(r);
    const gatesOk = gates.length > 0 && gates.every((g) => g.ok === true);
    const diag = diagnoses.find((s) => Number(s.round) === r);
    const llmId = `llm-${r}`;
    const gatesId = `gates-${r}`;
    if (llm) {
      nodes.push({
        id: llmId,
        kind: `LLM ${String(llm.task)} · ${String(llm.model)}`,
        title: `edit round ${r}`,
        at: fmtOffset(llm.t, start),
        tone: er && er.kind !== 'applied' ? 'bad' : undefined,
        row: row++,
        lane: 1,
        inputs: i === 0 ? ['composed prompt'] : ['prior source + failure feedback'],
        outputs: [
          files.length > 0 ? `${files.length} file(s) rewritten` : er ? String(er.kind) : 'reply',
        ],
        body: (
          <div style={{ fontSize: 11.5 }} title={typeof llm.promptPreview === 'string' ? llm.promptPreview : (files.join('\n') || undefined)}>
            <span className="muted">{llmStats(llm)}</span>
            {promptBtn(llm)}
          </div>
        ),
      });
      edges.push({ from: prev, to: llmId, ...(i > 0 ? {} : {}) });
    }
    if (gates.length > 0) {
      nodes.push({
        id: gatesId,
        kind: 'GATES',
        title: `gate battery · round ${r}`,
        at: gates[0] ? fmtOffset(gates[0].t, start) : undefined,
        tone: gatesOk ? 'ok' : 'bad',
        row: row++,
        lane: 1,
        body: (
          <>
            <GateChips gates={gates} />
            {diag && (
              <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>
                diagnosis: {String(diag.probesRun)} probe(s) · {diag.producedFix ? 'fix produced' : 'no fix'}
              </div>
            )}
          </>
        ),
        outputs: gatesOk ? ['PASS'] : ['FAIL → repair feedback'],
      });
      edges.push({ from: llm ? llmId : prev, to: gatesId, label: files.length ? `${files.length} files` : undefined });
      if (!gatesOk && i + 1 < effRounds.length) {
        edges.push({
          from: gatesId,
          to: `llm-${effRounds[i + 1]}`,
          label: diag?.producedFix ? 'failure + diagnosis fix' : 'failure feedback',
          tone: 'bad',
          back: true,
        });
        prev = gatesId; // forward chain continues from the failing gates into the next round via the loop edge
      } else {
        prev = gatesId;
      }
    } else if (llm) {
      prev = llmId;
    }
  });

  // copy-build gate battery (round 0)
  if (trace.mode === 'copy' && copyGates.length > 0) {
    const ok = copyGates.every((g) => g.ok === true);
    nodes.push({
      id: 'copy-gates',
      kind: 'GATES',
      title: 'gate battery (republish)',
      at: fmtOffset(copyGates[0].t, start),
      tone: ok ? 'ok' : 'bad',
      row: row++,
      lane: 1,
      body: <GateChips gates={copyGates} />,
      outputs: [ok ? 'PASS' : 'FAIL'],
    });
    edges.push({ from: prev, to: 'copy-gates' });
    prev = 'copy-gates';
  }

  // review / critic
  if (review || critic || qualityPasses.length > 0) {
    const parts: React.ReactNode[] = [];
    if (review) {
      const safe = safeties.length > 0 ? safeties.every((s) => s.safe) : true;
      parts.push(
        <div key="r" style={{ fontSize: 11.5 }} title={typeof review.promptPreview === 'string' ? review.promptPreview : undefined}>
          <span style={{ color: safe ? 'var(--ok, #30a46c)' : 'var(--danger, #e5484d)' }}>
            safety {safe ? '✓' : '✗'}
          </span>{' '}
          <span className="muted">
            {String(review.model)} · {llmStats(review)}
          </span>
          {promptBtn(review)}
        </div>,
      );
    }
    if (critic) {
      const score = qualityPasses[0]?.score;
      parts.push(
        <div key="c" style={{ fontSize: 11.5 }} title={typeof critic.promptPreview === 'string' ? critic.promptPreview : undefined}>
          <span className="muted">
            critic {String(critic.model)} · {llmStats(critic)}
          </span>
          {promptBtn(critic)}
        </div>,
      );
      if (score !== undefined && score !== null) {
        parts.push(
          <div key="q" className="muted" style={{ fontSize: 11.5 }}>
            quality score {String(score)} {qualityPasses[0]?.kept ? '(kept)' : ''}
          </div>,
        );
      }
    }
    nodes.push({
      id: 'quality',
      kind: `LLM review/critic${review ? ` · ${String(review.model)}` : ''}`,
      title: 'safety review · quality critic',
      at: review ? fmtOffset(review.t, start) : critic ? fmtOffset(critic.t, start) : undefined,
      row: row++,
      lane: 1,
      inputs: ['gated source'],
      outputs: ['verdict + score'],
      body: <>{parts}</>,
    });
    edges.push({ from: prev, to: 'quality' });
    prev = 'quality';
  }

  const codeRowsEnd = row; // asset lane spans alongside everything after inputs

  // ASSET TRACK (parallel lane)
  const hasAssets = outcomes.length > 0 || budget || inject;
  if (hasAssets) {
    nodes.push({
      id: 'assets',
      kind: 'ASSET GENERATION · gemini-2.5-flash-image + VLM judge',
      title: 'parallel art pipeline',
      tone: budget ? 'bad' : generated.length > 0 || reused.length > 0 ? 'ok' : undefined,
      row: 2,
      lane: 2,
      rowSpan: Math.max(codeRowsEnd - 2, 1),
      inputs: ['design entity sprite prompts'],
      outputs: [
        ...(generated.length > 0 ? [`${generated.length} generated: ${generated.join(', ')}`] : []),
        ...(reused.length > 0 ? [`${reused.length} reused: ${reused.join(', ')}`] : []),
        ...(banked.length > 0 ? [`${banked.length} banked: ${banked.join(', ')}`] : []),
        ...(preserved.length > 0 ? [`${preserved.length} preserved`] : []),
      ],
      body: budget ? (
        <div style={{ fontSize: 11.5, color: 'var(--danger, #e5484d)' }}>
          go-live budget expired at {fmtOffset(budget.t, start)} — banked{' '}
          {String(budget.banked ?? banked.length)} archetype(s)
        </div>
      ) : undefined,
    });
    edges.push({ from: 'inputs', to: 'assets', label: 'parallel' });
  }

  // JOIN / BIND
  if (inject || regateGates.length > 0) {
    nodes.push({
      id: 'bind',
      kind: 'BIND ASSETS',
      title: 'inject bindings · re-gate',
      at: inject ? fmtOffset(inject.t, start) : undefined,
      tone: inject?.injected ? 'ok' : inject ? 'bad' : undefined,
      row: row++,
      lane: 1,
      fullWidth: true,
      inputs: ['gated source', 'generated + attached art'],
      outputs: [
        inject?.injected ? 'render-manifest bindings injected' : 'bindings reverted',
        ...(unbound && Array.isArray(unbound.names)
          ? [`⚠ unbound: ${(unbound.names as string[]).join(', ')}`]
          : []),
      ],
      body: regateGates.length > 0 ? <GateChips gates={regateGates} /> : undefined,
    });
    edges.push({ from: prev, to: 'bind' });
    if (hasAssets) edges.push({ from: 'assets', to: 'bind', label: 'art' });
    prev = 'bind';
  } else if (hasAssets) {
    // no explicit inject step (rare) — assets converge on publish
    edges.push({ from: 'assets', to: 'publish', label: 'art' });
  }

  // PUBLISH
  nodes.push({
    id: 'publish',
    kind: 'PUBLISH',
    title: `→ ${trace.status}`,
    at: artifacts[0] ? fmtOffset(artifacts[0].t, start) : undefined,
    tone: trace.status === 'live' ? 'ok' : trace.status === 'failed' ? 'bad' : undefined,
    row: row++,
    lane: 1,
    fullWidth: true,
    outputs:
      artifacts.length > 0
        ? artifacts.map((a) => String(a.kind))
        : [trace.status === 'failed' ? 'no artifacts — build failed' : '—'],
    body:
      errors.length > 0 ? (
        <div style={{ fontSize: 11.5, color: 'var(--danger, #e5484d)' }}>
          {errors.map((e) => String(e.message)).join(' · ')}
        </div>
      ) : undefined,
  });
  edges.push({ from: prev, to: 'publish' });

  // POST-PUBLISH
  if (artCompletion || cover || suggest) {
    nodes.push({
      id: 'post',
      kind: suggest ? `POST · suggest ${String(suggest.model)}` : 'POST-PUBLISH',
      title: 'after go-live',
      row: row++,
      lane: 1,
      fullWidth: true,
      outputs: [
        ...(artCompletion
          ? [`art-completion enqueued (${String(artCompletion.jobs ?? '?')} job(s))`]
          : []),
        ...(cover ? [`cover art at ${fmtOffset(cover.t, start)}`] : []),
        ...(suggest ? ['iterate/asset prompt suggestions'] : []),
      ],
    });
    edges.push({ from: 'publish', to: 'post' });
  }

  return { nodes, edges };
}

// ── rendering ─────────────────────────────────────────────────────────────────

const NODE_STYLE: React.CSSProperties = {
  border: '1px solid var(--border, #334)',
  borderRadius: 8,
  padding: '7px 10px',
  background: 'var(--panel-2, rgba(20,26,40,0.92))',
  position: 'relative',
  zIndex: 1,
};

function NodeBox({ n }: { n: GraphNode }) {
  const edge =
    n.tone === 'bad'
      ? 'var(--danger, #e5484d)'
      : n.tone === 'ok'
        ? 'var(--ok, #30a46c)'
        : 'var(--accent, #37d0ff)';
  return (
    <div
      data-node={n.id}
      style={{
        ...NODE_STYLE,
        borderTop: `2px solid ${edge}`,
        gridRow: n.rowSpan ? `${n.row} / span ${n.rowSpan}` : String(n.row),
        gridColumn: n.fullWidth ? '1 / -1' : String(n.lane),
        alignSelf: 'start',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <span
          className="muted"
          style={{ fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase' }}
        >
          {n.kind}
        </span>
        {n.at && (
          <span className="muted" style={{ fontSize: 10.5 }}>
            {n.at}
          </span>
        )}
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 600, margin: '1px 0 2px' }}>{n.title}</div>
      {n.inputs?.map((line, i) => (
        <div key={`i${i}`} className="muted" style={{ fontSize: 11 }}>
          ◂ {line}
        </div>
      ))}
      {n.body}
      {n.outputs?.map((line, i) => (
        <div key={`o${i}`} style={{ fontSize: 11, color: 'var(--fg, #cfd6df)' }}>
          ▸ {line}
        </div>
      ))}
    </div>
  );
}

export function BuildCandidateTimeline({
  creationId,
  version,
  checkpoints,
}: {
  creationId: string;
  version: number;
  checkpoints: Step[];
}) {
  const services = useServices();
  const [launching, setLaunching] = useState<string | null>(null);
  const [error, setError] = useState<Record<string, string>>({});

  const launch = async (candidateId: string): Promise<void> => {
    setLaunching(candidateId);
    setError((current) => ({ ...current, [candidateId]: '' }));
    // Open while the click still carries transient user activation. Waiting for
    // the callable first lets popup blockers silently discard the Draft tab.
    const previewWindow = window.open('about:blank', '_blank');
    if (!previewWindow) {
      setError((current) => ({
        ...current,
        [candidateId]: 'Draft preview was blocked. Allow pop-ups for this site and try again.',
      }));
      setLaunching(null);
      return;
    }
    // The blank document has no script and remains same-origin until launch;
    // sever the opener synchronously before any network work or navigation.
    previewWindow.opener = null;
    try {
      const result = await services.callable.requestBuildDraftPreviewLaunch({
        creationId,
        version,
        candidateId,
      });
      previewWindow.location.replace(result.url);
    } catch (reason) {
      previewWindow.close();
      setError((current) => ({
        ...current,
        [candidateId]: reason instanceof Error ? reason.message : 'Draft preview unavailable.',
      }));
    } finally {
      setLaunching(null);
    }
  };

  if (checkpoints.length === 0) return null;
  return (
    <section aria-label="Build candidates" data-testid="build-candidate-timeline" className="build-candidates">
      <strong>Retained drafts</strong>
      <p className="muted build-candidates__disclaimer">
        Drafts are private intermediate bundles. They are not published, live, or quality-approved; online play and leaderboard writes are disabled.
      </p>
      <div className="build-candidates__rail">
        {checkpoints.map((candidate, index) => {
          const candidateId = typeof candidate.id === 'string' ? candidate.id : '';
          const remainingChecks = Array.isArray(candidate.remainingChecks)
            ? candidate.remainingChecks.filter((value): value is string => typeof value === 'string' && value.length > 0)
            : null;
          const previewable = candidate.previewAvailable === true;
          return (
            <article key={candidateId || String(index)} className="build-candidates__card" data-testid={`build-candidate-${candidateId || index}`}>
              <div className="build-candidates__heading">
                <strong>Draft</strong>
                <span>Round {String(candidate.round ?? '?')} · {String(candidate.state ?? 'retained')}</span>
              </div>
              <p>
                {candidate.hasRunnableBundle ? 'Compiled bundle retained' : 'Source retained'}
                {candidate.failureClass ? ` · ${String(candidate.failureClass)}` : ''}
              </p>
              <p className="muted" data-testid={`draft-checks-${candidateId || index}`}>
                {remainingChecks
                  ? remainingChecks.length > 0
                    ? `Checks still required: ${remainingChecks.join(', ')}`
                    : 'All required checks completed; publication is still a separate action.'
                  : 'Remaining checks unavailable for this historical draft.'}
              </p>
              {previewable && candidateId && (
                <button
                  type="button"
                  className="btn btn--primary btn--sm build-candidates__play"
                  disabled={launching !== null}
                  onClick={() => void launch(candidateId)}
                  data-testid={`draft-play-${candidateId}`}
                >
                  {launching === candidateId ? 'Opening Draft…' : 'Play Draft ↗'}
                </button>
              )}
              {candidate.hasRunnableBundle === true && !previewable && (
                <p className="muted build-candidates__unavailable">Private preview is not available for this retained bundle.</p>
              )}
              {error[candidateId] && <p className="error-text" role="alert">{error[candidateId]}</p>}
              {Array.isArray(candidate.uncoveredCriticalVerbs) && candidate.uncoveredCriticalVerbs.length > 0 && (
                <p className="muted">Missing: {(candidate.uncoveredCriticalVerbs as Array<{ verb?: unknown }>).map(v => String(v.verb)).join(', ')}</p>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

export function BuildTraceGraph({ creationId, version, trace, redacted }: { creationId: string; version: number; trace: BuildTraceView; redacted: boolean }) {
  const services = useServices();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [paths, setPaths] = useState<
    Array<{ d: string; tone?: 'bad' | 'muted'; label?: string; lx: number; ly: number }>
  >([]);
  const [showRaw, setShowRaw] = useState(false);
  const start = trace.startedAt;
  const llmSteps = trace.steps.filter((s) => s.type === 'llm');
  const total = trace.finishedAt ? trace.finishedAt - trace.startedAt : null;
  const spend = llmSteps.reduce(
    (sum, s) => sum + (typeof s.estimatedCostUsd === 'number' ? s.estimatedCostUsd : 0),
    0,
  );
  const checkpoints = trace.steps.filter((s) => s.type === 'candidate-checkpoint');

  const openPrompt = (gcsUri: string): void => {
    void services.callable
      .resolveLlmPromptUrl({ gcsUri })
      .then(({ url }) => window.open(url, '_blank', 'noopener'))
      .catch(() => {});
  };

  const { nodes, edges } = buildGraph(trace, openPrompt);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const draw = (): void => {
      const box = el.getBoundingClientRect();
      const rect = (id: string): DOMRect | null =>
        el.querySelector(`[data-node="${id}"]`)?.getBoundingClientRect() ?? null;
      const next: Array<{ d: string; tone?: 'bad' | 'muted'; label?: string; lx: number; ly: number }> = [];
      for (const e of edges) {
        const a = rect(e.from);
        const b = rect(e.to);
        if (!a || !b) continue;
        if (e.back) {
          // repair loop: out of the source's LEFT edge, around the gutter, into the target's LEFT edge
          const sx = a.left - box.left;
          const sy = a.top - box.top + a.height / 2;
          const tx = b.left - box.left;
          const ty = b.top - box.top + b.height / 2;
          const gx = Math.max(Math.min(sx, tx) - 26, 6);
          next.push({
            d: `M ${sx} ${sy} C ${gx} ${sy}, ${gx} ${ty}, ${tx} ${ty}`,
            tone: e.tone ?? 'bad',
            label: e.label,
            lx: gx + 4,
            ly: (sy + ty) / 2,
          });
        } else {
          const sx = a.left - box.left + a.width / 2;
          const sy = a.bottom - box.top;
          const tx = b.left - box.left + b.width / 2;
          const ty = b.top - box.top;
          const my = (sy + ty) / 2;
          next.push({
            d: `M ${sx} ${sy} C ${sx} ${my}, ${tx} ${my}, ${tx} ${ty}`,
            tone: e.tone,
            label: e.label,
            lx: (sx + tx) / 2 + 6,
            ly: my,
          });
        }
      }
      setPaths(next);
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trace]);

  const maxRow = Math.max(...nodes.map((n) => n.row + (n.rowSpan ?? 1) - 1));
  const hasLane2 = nodes.some((n) => n.lane === 2 && !n.fullWidth);

  return (
    <div data-testid="build-trace-flow" style={{ margin: '8px 0 0 4px' }}>
      <p className="muted" style={{ fontSize: 12, margin: '0 0 8px' }}>
        {trace.mode} build · attempt {trace.attempt} ·{' '}
        {trace.mode === 'copy' || llmSteps.length === 0
          ? 'no LLM'
          : trace.engine
            ? `${trace.engine.provider}/${trace.engine.model}`
            : '—'}
        {trace.engine?.actual ? ` (ran on ${trace.engine.actual.provider}/${trace.engine.actual.model})` : ''}
        {' · '}
        {trace.status}
        {total !== null ? ` · ${fmtDur(total)} total` : ''} · {llmSteps.length} LLM call(s)
        {spend > 0 ? ` · ${fmtCost(spend)}` : ''} · started {fmtClock(start)}
        {redacted ? ' · prompts hidden' : ''}
      </p>

      {/* the graph: grid of nodes + a measured SVG edge overlay. min-width keeps
          the two-lane shape on phones (the container pans sideways). */}
      <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
        <div
          ref={containerRef}
          style={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: hasLane2 ? 'minmax(340px, 3fr) minmax(240px, 2fr)' : 'minmax(320px, 1fr)',
            gridTemplateRows: `repeat(${maxRow}, auto)`,
            gap: '26px 34px',
            minWidth: hasLane2 ? 640 : 360,
            maxWidth: 980,
            padding: '2px 2px 2px 10px',
          }}
        >
          <svg
            aria-hidden
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}
          >
            <defs>
              <marker id="bt-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M 0 0 L 8 4 L 0 8 z" fill="var(--border, #667)" />
              </marker>
              <marker id="bt-arrow-bad" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M 0 0 L 8 4 L 0 8 z" fill="var(--danger, #e5484d)" />
              </marker>
            </defs>
            {paths.map((p, i) => (
              <g key={i}>
                <path
                  d={p.d}
                  fill="none"
                  stroke={p.tone === 'bad' ? 'var(--danger, #e5484d)' : 'var(--border, #667)'}
                  strokeWidth={1.5}
                  strokeDasharray={p.tone === 'bad' ? '4 3' : undefined}
                  markerEnd={p.tone === 'bad' ? 'url(#bt-arrow-bad)' : 'url(#bt-arrow)'}
                />
                {p.label && (
                  <text x={p.lx} y={p.ly} style={{ fontSize: 10, fill: p.tone === 'bad' ? 'var(--danger, #e5484d)' : 'var(--muted-fg, #8b94a3)' }}>
                    {p.label}
                  </text>
                )}
              </g>
            ))}
          </svg>
          {nodes.map((n) => (
            <NodeBox key={n.id} n={n} />
          ))}
        </div>
      </div>

      <BuildCandidateTimeline creationId={creationId} version={version} checkpoints={checkpoints} />

      {/* raw chronological projection of the same steps */}
      <details style={{ marginTop: 10 }} onToggle={(e) => setShowRaw((e.target as HTMLDetailsElement).open)}>
        <summary className="muted" style={{ cursor: 'pointer', fontSize: 12 }}>
          Raw timeline ({trace.steps.length} steps)
        </summary>
        {showRaw &&
          trace.steps.map((s, i) => (
            <div
              key={i}
              style={{ display: 'flex', gap: 8, padding: '2px 0', borderLeft: '2px solid var(--border, #333)', paddingLeft: 10, fontSize: 11.5 }}
            >
              <span className="muted" style={{ minWidth: 52, textAlign: 'right' }}>
                {fmtOffset(s.t, start)}
              </span>
              <span className="muted">
                <strong style={{ color: 'var(--fg, #ddd)' }}>{s.type}</strong>{' '}
                {JSON.stringify({ ...s, t: undefined, type: undefined, promptPreview: undefined }).slice(0, 160)}
              </span>
            </div>
          ))}
      </details>
    </div>
  );
}
